import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Users, BookOpen, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import { OrganizationSchema, WebsiteSchema } from '../components/Schema';
import { trackConversion } from '../utils/analytics';
import PopularPosts from '../components/PopularPosts';
import BannerAd from '../components/BannerAd';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email });
      toast({
        title: "Success!",
        description: response.data.message || "You've been subscribed to our newsletter.",
      });
      
      // Track conversion
      trackConversion('newsletter', { email });
      
      setEmail('');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Something went wrong. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Truck Accident Prevention and Safety Resources"
        description="Professional guidance for truck drivers, fleet owners, and safety managers. Learn accident prevention strategies, driver safety best practices, and legal protection for your trucking operations."
        canonical="/"
        keywords="truck accident prevention, driver safety training, DOT compliance, fleet management, trucking safety, commercial vehicle accidents, truck driver protection, fleet safety programs"
      />
      <OrganizationSchema />
      <WebsiteSchema />
      
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-gray-900/80 border border-orange-500/30 rounded-full px-4 py-2 mb-6">
              <Shield className="h-4 w-4 text-orange-400" />
              <span className="text-sm font-medium text-orange-300">Professional Safety Resource</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Truck Accident Prevention and Safety Resources{' '}
              <span className="text-gradient">All in One Place</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Practical guidance for truck drivers, fleet owners, and safety managers on how to prevent accidents, 
              protect drivers, and reduce legal risk.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg" 
                className="btn-primary text-white font-semibold px-8 h-12"
                onClick={() => document.getElementById('newsletter').scrollIntoView({ behavior: 'smooth' })}
              >
                Subscribe for Safety Alerts
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-orange-500 text-orange-400 hover:bg-orange-500/10 h-12 px-8"
                asChild
              >
                <Link to="/resources">
                  Explore Resources
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What This Site Provides */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Clear, Actionable Information for the Trucking Industry
            </h2>
            <p className="text-lg text-gray-300">
              Truck accidents can change lives and businesses in seconds. This site is dedicated to helping 
              trucking professionals reduce risk, stay compliant, and protect themselves legally through 
              education and awareness.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: AlertTriangle,
                title: 'Accident Prevention Strategies',
                description: 'Learn proven methods to identify and mitigate risks before they become incidents.'
              },
              {
                icon: Users,
                title: 'Driver Safety Education',
                description: 'Comprehensive training resources and best practices for safe driving operations.'
              },
              {
                icon: Shield,
                title: 'Legal and Compliance Insights',
                description: 'Stay informed about regulations, liability protection, and legal requirements.'
              },
              {
                icon: TrendingUp,
                title: 'Industry News and Analysis',
                description: 'Keep up with the latest trends, regulations, and safety innovations.'
              },
              {
                icon: BookOpen,
                title: 'Downloadable Resources',
                description: 'Access checklists, templates, and guides to implement in your operations.'
              },
              {
                icon: CheckCircle,
                title: 'Real-World Case Studies',
                description: 'Learn from actual incidents and understand what works in prevention.'
              }
            ].map((item, index) => (
              <Card key={index} className="card-hover bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/10 mb-4">
                    <item.icon className="h-6 w-6 text-orange-400" />
                  </div>
                  <CardTitle className="text-white">{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-400">{item.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Posts Section */}
      <PopularPosts variant="horizontal" limit={5} showViewCount={true} />

      {/* Who This Is For */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
              Who This Is For
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Truck drivers (company and owner-operators)',
                'Fleet owners and managers',
                'Safety directors',
                'Dispatchers and operations managers',
                'Anyone responsible for driver safety and compliance'
              ].map((audience, index) => (
                <div key={index} className="flex items-start space-x-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                  <CheckCircle className="h-6 w-6 text-orange-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200 text-lg">{audience}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="section-padding bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="newsletter-bg rounded-2xl p-8 md:p-12">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Stay Informed. Stay Protected.
                </h2>
                <p className="text-gray-300 text-lg">
                  Sign up for our newsletter to receive:
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {[
                  'Safety alerts and prevention tips',
                  'Legal updates affecting trucking',
                  'New resources and guides',
                  'Industry news and analysis'
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3 bg-gray-800/50 rounded-lg px-4 py-3 border border-gray-700/50">
                    <CheckCircle className="h-5 w-5 text-orange-400 flex-shrink-0" />
                    <span className="text-white font-medium">{benefit}</span>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 h-12 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                  required
                />
                <Button 
                  type="submit" 
                  size="lg" 
                  className="btn-primary text-white font-semibold px-8 h-12"
                  disabled={loading}
                >
                  {loading ? 'Subscribing...' : 'Subscribe Now'}
                </Button>
              </form>

              <p className="text-gray-400 text-sm text-center mt-4">
                We respect your privacy. No spam. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Content Preview */}
      <section className="section-padding bg-gray-950">
        <div className="container mx-auto px-4">
          {/* Banner Ad */}
          <BannerAd position="between_content" page="home" className="mb-12" />
          
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Latest From Our Blog
            </h2>
            <Button variant="ghost" className="text-orange-400 hover:text-orange-300" asChild>
              <Link to="/blog">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Common Causes of Truck Accidents',
                category: 'Accident Prevention',
                excerpt: 'Understanding the most frequent causes is the first step in prevention.'
              },
              {
                title: 'Driver Training Best Practices',
                category: 'Safety Training',
                excerpt: 'Investment in training reduces accidents and insurance costs.'
              },
              {
                title: 'Post-Accident Documentation',
                category: 'Legal Protection',
                excerpt: 'The first minutes after an accident are critical for legal protection.'
              }
            ].map((post, index) => (
              <Card key={index} className="card-hover bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="badge badge-orange mb-3">{post.category}</div>
                  <CardTitle className="text-white text-xl">{post.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-400 mb-4">{post.excerpt}</CardDescription>
                  <Button variant="link" className="text-orange-400 hover:text-orange-300 p-0" asChild>
                    <Link to="/blog">
                      Read More
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
